const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/v1'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

class ApiClient {
  private getToken(): string | null {
    return localStorage.getItem('etx_access_token')
  }

  setToken(token: string) {
    localStorage.setItem('etx_access_token', token)
  }

  clearToken() {
    localStorage.removeItem('etx_access_token')
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = this.getToken()
    const isFormData = options.body instanceof FormData
    const headers: HeadersInit = {
      ...(!isFormData && { 'Content-Type': 'application/json' }),
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    }

    const response = await fetch(`${BASE_URL}${path}`, { ...options, headers })

    // `/public/*` is the no-login consumer surface (shop links, intake,
    // tracking). A 401 there must NOT bounce the consumer to /login — they
    // have no account. `/auth/*` is exempt for the same don't-redirect reason.
    if (
      response.status === 401 &&
      !path.startsWith('/auth/') &&
      !path.startsWith('/public/')
    ) {
      this.clearToken()
      window.location.href = '/login'
      throw new ApiError(401, 'Unauthorized')
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new ApiError(response.status, extractErrorMessage(error))
    }

    if (response.status === 204) return undefined as T
    return response.json()
  }

  /** `signal` lets TanStack abort a superseded fetch, so a slow older
   *  response can never land on top of a fresher one. */
  get<T>(path: string, params?: Record<string, string | number | boolean | undefined>, signal?: AbortSignal) {
    if (params) {
      const entries = Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][]
      if (entries.length) {
        path += '?' + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()
      }
    }
    return this.request<T>(path, { signal })
  }

  /** `signal` lets a caller give up on a stalled request (e.g. a timeout). */
  post<T>(path: string, body?: unknown, signal?: AbortSignal) {
    const isFormData = body instanceof FormData
    return this.request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
      signal,
    })
  }

  patch<T>(path: string, body: unknown) {
    return this.request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  }

  put<T>(path: string, body: unknown) {
    return this.request<T>(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  }

  // DELETE may carry a JSON body when the BE needs disambiguating data
  // (e.g. `disconnectBodyshop` requires `bodyshopId` so a stale client
  // can't accidentally remove a re-linked shop). The BE controllers
  // explicitly read these via `@Body()` and the OpenAPI spec documents
  // them — fetch and Node's HTTP server pass DELETE bodies through
  // unchanged. Some old proxies/CDNs strip them; if a deploy ever sits
  // behind one, switch the affected endpoint to a query param.
  delete<T>(path: string, body?: unknown, signal?: AbortSignal) {
    return this.request<T>(path, {
      method: 'DELETE',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    })
  }

  // Binary GET — used by file exports (PDF, etc). The BE sends
  // Content-Disposition so the browser/user gets the correct filename;
  // we surface that here so callers don't each re-parse the header.
  async getBlob(path: string): Promise<{ blob: Blob; filename: string | null }> {
    const token = this.getToken()
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {}
    const response = await fetch(`${BASE_URL}${path}`, { headers })

    if (response.status === 401 && !path.startsWith('/public/')) {
      this.clearToken()
      window.location.href = '/login'
      throw new ApiError(401, 'Unauthorized')
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new ApiError(response.status, extractErrorMessage(error))
    }

    return { blob: await response.blob(), filename: parseFilename(response.headers.get('content-disposition')) }
  }
}

export const api = new ApiClient()

// NestJS + class-validator returns `message` as a nested array of
// validation nodes — coerced naively, that becomes "[object Object]"
// in toasts. Walk the tree, pull out the human-readable `constraints`
// strings, fall back to the BE's `error` field, then to a generic.
function extractErrorMessage(payload: unknown): string {
  if (!payload || typeof payload !== 'object') return 'Request failed'
  const body = payload as { message?: unknown; error?: unknown }
  if (typeof body.message === 'string') return body.message
  if (Array.isArray(body.message)) {
    if (body.message.every((m) => typeof m === 'string')) {
      return (body.message as string[]).join('. ')
    }
    const flat: string[] = []
    const walk = (node: unknown) => {
      if (!node || typeof node !== 'object') return
      const n = node as { constraints?: unknown; children?: unknown }
      if (n.constraints && typeof n.constraints === 'object') {
        for (const v of Object.values(n.constraints as Record<string, unknown>)) {
          if (typeof v === 'string') flat.push(v)
        }
      }
      if (Array.isArray(n.children)) n.children.forEach(walk)
    }
    body.message.forEach(walk)
    if (flat.length) return flat.join('. ')
  }
  if (typeof body.error === 'string') return body.error
  return 'Request failed'
}

// Pull `filename` out of a Content-Disposition header. Handles both
// quoted (`filename="x.pdf"`) and bare (`filename=x.pdf`) forms, plus
// RFC 5987 `filename*=UTF-8''x.pdf` (preferred when present, since it
// supports non-ASCII names — Express's res.download emits this when
// the filename has special chars).
function parseFilename(header: string | null): string | null {
  if (!header) return null
  const ext = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i)
  if (ext?.[1]) {
    try { return decodeURIComponent(ext[1].trim()) } catch { /* fall through */ }
  }
  const m = header.match(/filename="?([^";]+)"?/i)
  return m?.[1]?.trim() ?? null
}
